import { create } from "zustand";
import { toast } from "react-toastify";
import {
  addSlaUserMatrix,
  getAllSlaLevels,
  getSlaUsers,
  getSlaUserMatrixList,
  updateSlaUserMatrix,
} from "../../../../Apis/complaintApi";
import {
  COMPLAINT_MAX_FETCH_SIZE,
  paginateClientList,
  sortActiveFirst,
  buildClientPagination,
} from "../../utils/complaintListUtils";

const formatLevelName = (levelName) => {
  if (levelName == null || levelName === "") return "";
  const stringValue = String(levelName).trim();
  if (/^level\s/i.test(stringValue)) return stringValue;
  return `Level ${stringValue}`;
};

const mapSlaUserMatrixItem = (item) => ({
  slaUserMatrixId:
    item.tMatrixId ?? item.slaUserMatrixId ?? item.slaUserMatrixMatrixId ?? item.id,
  tMatrixId: item.tMatrixId ?? item.slaUserMatrixId ?? item.id,
  zoneId: item.zoneId ?? "",
  zoneName: item.zoneName ?? "",
  circleId: item.wardId ?? item.circleId ?? "",
  circleIds: item.wardId ? [String(item.wardId)] : item.circleIds ?? [],
  wardNo: item.wardName ?? item.wardNo ?? "",
  wardName: item.wardName ?? item.wardNo ?? "",
  userId: item.userId ?? "",
  userName: item.userName ?? "",
  mobileNo: item.mobileNo ?? "",
  loginId: item.loginId ?? "",
  levelId: item.levelId ?? item.level ?? "",
  level: formatLevelName(item.levelName ?? item.level),
  levelName: item.levelName ?? item.level ?? "",
  alertBeforeEscalationMin:
    item.alertBeforeEscalationInMin ?? item.alertBeforeEscalationMin ?? "",
  escalationDurationMin:
    item.escalationDurationInMin ?? item.escalationDurationMin ?? "",
  isActive: Boolean(item.isActive),
  status: item.status ?? "",
  lastModifiedOn: item.lastModifiedOn ?? item.modifiedDate ?? "",
});

const mapSlaUserMatrixList = (response) => {
  if (!response) return [];

  const list =
    response?.data ??
    response?.result ??
    response?.items ??
    (Array.isArray(response) ? response : []);

  return Array.isArray(list) ? list.map(mapSlaUserMatrixItem) : [];
};

const mapSelectOptions = (items = []) =>
  items.map((item) => ({
    value: item.value ?? item.levelId ?? item.id ?? "",
    text: item.text ?? item.levelName ?? item.name ?? "",
  }));

const mapSlaUserOptions = (items = []) =>
  items.map((item) => ({
    userId: item.userId ?? item.value ?? item.id ?? "",
    userName: item.userName ?? item.text ?? item.name ?? "",
    employeeCode: item.employeeCode ?? item.loginId ?? "",
  }));

export const DEFAULT_REPORT_ZONE_ID = "1";
export const DEFAULT_REPORT_ZONE_NAME = "Delhi Central Zone";

const getErrorMessage = (
  error,
  fallback = "Unable to save SLA user matrix details."
) => {
  const responseData = error?.response?.data;
  if (typeof responseData === "string" && responseData) return responseData;
  return responseData?.message || responseData?.Message || fallback;
};

const isApiSuccess = (response) => {
  if (response?.success === false) return false;
  if (response?.success === true) return true;
  if (response?.tMatrixId) return true;
  return Boolean(response?.message);
};

export const useSlaUserMatrixStore = create((set, get) => ({
  slaUserMatrixList: [],
  filteredSlaUserMatrixList: [],
  levelOptions: [],
  userOptions: [],
  isLoading: false,
  isSaving: false,
  filters: {
    searchTerm: "",
    zoneId: DEFAULT_REPORT_ZONE_ID,
    circleId: "",
  },
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  },

  updateFilter: (name, value) =>
    set((state) => ({
      filters: { ...state.filters, [name]: value },
    })),

  fetchMasterOptions: async () => {
    try {
      const [levels, users] = await Promise.all([
        getAllSlaLevels(),
        getSlaUsers(),
      ]);

      set({
        levelOptions: mapSelectOptions(levels),
        userOptions: mapSlaUserOptions(users),
      });

      return { success: true };
    } catch (error) {
      console.error("Error fetching SLA user matrix master options:", error);
      set({ levelOptions: [], userOptions: [] });
      return { success: false, error };
    }
  },

  resetFilters: async () => {
    set({
      filters: {
        searchTerm: "",
        zoneId: DEFAULT_REPORT_ZONE_ID,
        circleId: "",
      },
    });
    return get().fetchSlaUserMatrixList({
      currentPage: 1,
      searchTerm: "",
      zoneId: DEFAULT_REPORT_ZONE_ID,
      circleId: "",
    });
  },

  fetchSlaUserMatrixList: async (options = {}) => {
    const { filters, pagination } = get();
    const currentPage = options.currentPage ?? pagination.currentPage;
    const pageSize = options.pageSize ?? pagination.pageSize;
    const searchTerm = (options.searchTerm ?? filters.searchTerm)?.trim() ?? "";
    const zoneId = options.zoneId ?? filters.zoneId;
    const circleId = options.circleId ?? filters.circleId;

    set({
      isLoading: true,
      filters: { searchTerm, zoneId, circleId },
    });

    try {
      const response = await getSlaUserMatrixList({
        pageIndex: 1,
        pageSize: COMPLAINT_MAX_FETCH_SIZE,
        search: searchTerm,
        zoneId,
        wardId: circleId,
      });

      const sorted = sortActiveFirst(mapSlaUserMatrixList(response));
      const pagedRecords = paginateClientList(sorted, currentPage, pageSize);
      const nextPagination = buildClientPagination(
        sorted.length,
        currentPage,
        pageSize
      );

      set({
        slaUserMatrixList: sorted,
        filteredSlaUserMatrixList: pagedRecords,
        pagination: nextPagination,
        isLoading: false,
      });

      return { success: true, data: pagedRecords, pagination: nextPagination };
    } catch (error) {
      console.error("Error fetching SLA user matrix list:", error);

      set({
        slaUserMatrixList: [],
        filteredSlaUserMatrixList: [],
        pagination: { ...get().pagination, totalCount: 0 },
        isLoading: false,
      });

      return { success: false, error };
    }
  },

  saveSlaUserMatrix: async (payload, isEdit = false) => {
    set({ isSaving: true });

    try {
      const response = isEdit
        ? await updateSlaUserMatrix(payload)
        : await addSlaUserMatrix(payload);

      if (!isApiSuccess(response)) {
        const message =
          response?.message || "Unable to save SLA user matrix details.";
        toast.error(message);
        return { success: false, message };
      }

      toast.success(
        response?.message ||
          (isEdit
            ? "SLA user matrix updated successfully."
            : "SLA user matrix added successfully.")
      );

      await get().fetchSlaUserMatrixList({
        currentPage: isEdit ? get().pagination.currentPage : 1,
      });

      return { success: true, message: response?.message };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return { success: false, message, error };
    } finally {
      set({ isSaving: false });
    }
  },
}));
